import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface AlertButton {
  label: string;
  variant?: 'primary' | 'secondary' | 'danger';
  onClick?: () => void;
}

interface AlertOptions {
  title?: string;
  message: string;
  variant?: 'info' | 'success' | 'warning' | 'error';
  buttons?: AlertButton[];
}

interface ConfirmOptions {
  title?: string;
  message: string;
  variant?: 'info' | 'warning' | 'danger';
  confirmLabel?: string;
  cancelLabel?: string;
}

interface AlertContextType {
  alert: (options: AlertOptions | string) => Promise<void>;
  confirm: (options: ConfirmOptions | string) => Promise<boolean>;
  isOpen: boolean;
  alertState: AlertState | null;
  closeAlert: (result?: boolean) => void;
}

interface AlertState {
  type: 'alert' | 'confirm';
  title?: string;
  message: string;
  variant: 'info' | 'success' | 'warning' | 'error' | 'danger';
  buttons: AlertButton[];
  resolve: (value?: boolean) => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const AlertProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [alertState, setAlertState] = useState<AlertState | null>(null);

  const closeAlert = useCallback((result?: boolean) => {
    if (alertState) {
      alertState.resolve(result);
      setAlertState(null);
    }
  }, [alertState]);

  const alert = useCallback((options: AlertOptions | string): Promise<void> => {
    return new Promise((resolve) => {
      const opts = typeof options === 'string' ? { message: options } : options;

      setAlertState({
        type: 'alert',
        title: opts.title,
        message: opts.message,
        variant: opts.variant || 'info',
        buttons: opts.buttons || [{ label: 'OK', variant: 'primary' }],
        resolve: () => resolve(),
      });
    });
  }, []);

  const confirm = useCallback((options: ConfirmOptions | string): Promise<boolean> => {
    return new Promise((resolve) => {
      const opts = typeof options === 'string' ? { message: options } : options;

      setAlertState({
        type: 'confirm',
        title: opts.title,
        message: opts.message,
        variant: opts.variant || 'warning',
        buttons: [
          { label: opts.cancelLabel || 'Cancel', variant: 'secondary' },
          { label: opts.confirmLabel || 'Confirm', variant: opts.variant === 'danger' ? 'danger' : 'primary' },
        ],
        resolve: (value?: boolean) => resolve(value ?? false),
      });
    });
  }, []);

  return (
    <AlertContext.Provider value={{ alert, confirm, isOpen: !!alertState, alertState, closeAlert }}>
      {children}
    </AlertContext.Provider>
  );
};

export const useAlert = (): AlertContextType => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};
